import { useState } from 'react';
import { token } from '../../tokens/tokens';
import { ChartTooltip, type ChartTooltipComparison } from '../ChartTooltip/ChartTooltip';
import styles from './Gauge.module.css';

/**
 * A bullet gauge — one reading placed on a bounded, qualitatively banded scale.
 *
 * Figma: the NPS card (912:35258), which as of 2026-09-16 draws this directly
 * rather than leaving it derived. Its `Progress Bar` is **60** segments of 4x48
 * across the full 554 chart slot, square-cornered, and its readout is a
 * left-aligned row — figure then caption, `space/4` apart — sitting with the bar
 * as a block centred in the slot's 224 height.
 *
 * What the file still does not draw, and what therefore stays derived: the
 * arbitrary domain, the caller-defined bands, the scale row and the hover
 * readout. `ProgressBar` remains the ancestor of the tones — the bar is still
 * "segments coloured by where they sit, on the `component/progress` tones" — but
 * the geometry now comes from the NPS card, not from the atom. See plan.md §7.
 *
 * `ProgressBar` itself is untouched: it maps 1:1 onto Figma `334:18792`, and its
 * 30 segments and 5/15 boundaries are that component's, not this one's.
 *
 * ```tsx
 * <Gauge
 *   value={34}
 *   min={-100}
 *   max={100}
 *   bands={[
 *     { from: -100, tone: 'negative' },
 *     { from: 0, tone: 'neutral' },
 *     { from: 30, tone: 'positive' },
 *   ]}
 *   caption="NPS"
 *   formatValue={(v) => (v > 0 ? `+${v}` : String(v))}
 * />
 * ```
 */

/**
 * Figma's NPS card draws 60 rectangles (912:35320); the count is structural, not
 * a token. Double `ProgressBar`'s 30 — at 554 wide, 30 segments would leave
 * 14px gaps and read as a dotted line, and the finer count is what lets a
 * single segment mean 3.3 NPS points rather than 6.7.
 */
const SEGMENT_COUNT = 60;

export type GaugeTone = 'negative' | 'neutral' | 'positive';

/**
 * One band of the scale, running from `from` up to the next band's `from` — or
 * to `max` for the last one.
 */
export type GaugeBand = {
  /** Lower bound, in the gauge's own units. */
  from: number;
  tone: GaugeTone;
  /**
   * What this stretch of the scale is called — "Detractors", "At risk". Shown
   * in the hover readout, which is the only place the bands are named: the bar
   * says them in colour and colour alone is not a label.
   *
   * Falls back to the tone, capitalised, so a band list written without labels
   * still reads rather than showing nothing.
   */
  label?: string;
};

export type GaugeProps = {
  /** The reading. Values outside `min`–`max` are clamped for the bar. */
  value: number;
  /**
   * Switches the gauge to a **zero-anchored** scale, the shape the NPS card
   * takes: the domain becomes `0…+bound` when the reading is positive and
   * `−bound…0` when it is negative, and the fill grows *out of zero* — from the
   * left edge going right when positive, from the right edge going left when
   * negative. So zero is always the origin the eye starts from and the fill's
   * length is the magnitude, whichever way the score has gone.
   *
   * It replaces `min`, `max` and `bands`, all of which are ignored when it is
   * set. `bands` in particular has nothing to say here: every segment on a
   * zero-anchored scale sits on one side of zero, so the fill is one tone —
   * `positive` above, `negative` below — rather than a ramp across thresholds.
   *
   * Left-fill for a negative reading was the alternative and is wrong: it would
   * anchor the bar at −bound, so a score of −0.1 would draw an almost-full bar
   * and −9.9 an almost-empty one, which is backwards.
   */
  bound?: number;
  /**
   * The scale's bands, lowest first. **Required, with no default**: inheriting
   * `ProgressBar`'s 5/15-of-30 boundaries would put them at 16.7% and 50% of
   * whatever range the caller passed, which is a plausible-looking number that
   * means nothing. A gauge with no stated bands is a bar.
   *
   * Sorted defensively here, so a list written out of order still renders
   * correctly rather than silently mis-colouring the scale.
   */
  bands?: GaugeBand[];
  /** Bottom of the scale. */
  min?: number;
  /** Top of the scale. */
  max?: number;
  /**
   * The caption *beside* the figure — Figma puts it to the right, not above.
   * Names what the number is, so "+34" reads as "+34 NPS".
   */
  caption?: string;
  /**
   * The reading over the comparison period — "vs +12.0" and a change badge in
   * the hover readout. Absent with comparison off.
   */
  compare?: ChartTooltipComparison;
  /**
   * Formats the figure and both scale ends, so the three can never disagree.
   * The same contract every chart's `formatValue` has.
   */
  formatValue?: (value: number) => string;
  /**
   * Accessible name. Falls back to `caption`. A meter with no name is announced
   * as a bare number.
   */
  label?: string;
  className?: string;
};

export function Gauge({
  value,
  bands = [],
  bound,
  min = 0,
  max = 100,
  caption,
  compare,
  formatValue,
  label,
  className,
}: GaugeProps) {
  const format = formatValue ?? ((v: number) => String(v));

  /*
   * The scale's two ends. On a zero-anchored scale they are 0 and ±bound —
   * whole by construction — so they are written as whole signed numbers rather
   * than pushed through `format`: that formatter exists for the *reading*, and
   * a metric that shows a tenth on its figure would otherwise label the ends
   * "-10.0" and "0.0", which is precision about a number that has none to give.
   * A banded scale keeps `format`, since its ends are whatever the caller set.
   */
  const formatEnd = (v: number) =>
    bound === undefined ? format(v) : `${v > 0 ? '+' : ''}${Math.round(v)}`;

  /*
   * A zero-anchored gauge derives its own domain from the reading's sign, so a
   * caller that passes `bound` never passes `min`/`max` — see the prop.
   */
  const signed = bound !== undefined && bound > 0;
  const negative = signed && value < 0;
  const lo = signed ? (negative ? -bound! : 0) : min;
  const hi = signed ? (negative ? 0 : bound!) : max;

  /*
   * A zero-width domain would divide by nothing on every segment. It is a
   * caller error rather than a state to render, but drawing an empty track is
   * more use than an `NaN` bar full of blank segments.
   */
  const span = hi - lo;
  const usable = span > 0;

  const clamped = Math.min(hi, Math.max(lo, value));

  /*
   * How many segments the fill covers. On a zero-anchored scale it is the
   * magnitude — the distance from zero — because zero is an *end* of the
   * domain rather than a point inside it; on a plain scale it is the distance
   * from the bottom of the range, which is the same formula with `lo` as the
   * origin.
   */
  const origin = signed ? 0 : lo;
  const filled = usable ? Math.round((Math.abs(clamped - origin) / span) * SEGMENT_COUNT) : 0;

  const sorted = [...bands].sort((a, b) => a.from - b.from);

  /*
   * Tone comes from where a segment *sits*, not from the current value — the
   * same rule `ProgressBar` uses, and the reason the filled run reads as a
   * journey across the scale rather than as one flat colour that changes.
   * A segment is judged at its midpoint, so it belongs to the band it mostly
   * occupies rather than to whichever one its left edge lands in.
   */
  const toneAt = (index: number): GaugeTone => {
    /*
     * One tone for the whole fill on a zero-anchored scale: the bar says which
     * side of zero the score is on, and every filled segment is on that side.
     */
    if (signed) return negative ? 'negative' : 'positive';
    const midpoint = lo + ((index + 0.5) / SEGMENT_COUNT) * span;
    let tone: GaugeTone = sorted[0]?.tone ?? 'neutral';
    for (const band of sorted) {
      if (midpoint >= band.from) tone = band.tone;
    }
    return tone;
  };

  const toneClass: Record<GaugeTone, string> = {
    negative: styles.negative,
    neutral: styles.neutral,
    positive: styles.positive,
  };

  /*
   * The same three tones as *text* colours. Separate from `toneClass` because
   * that one paints a segment's background — reusing it on the figure would
   * give the number a coloured box rather than coloured ink.
   */
  const valueToneClass: Record<GaugeTone, string> = {
    negative: styles.valueNegative,
    neutral: styles.valueNeutral,
    positive: styles.valuePositive,
  };

  /*
   * The same three tones as colour values, for the tooltip's swatch. The only
   * place a `component/progress` token crosses into JS here — the bar itself is
   * coloured by class, where stylelint can see it.
   */
  const toneColor: Record<GaugeTone, string> = {
    negative: token['progress-negative-surface'],
    neutral: token['progress-neutral-surface'],
    positive: token['progress-positive-surface'],
  };

  /**
   * The band the reading falls in — what the tooltip names. Undefined on a
   * zero-anchored scale, which has no bands; the tooltip falls back to the
   * tone's own name there.
   */
  const bandAt = (v: number): GaugeBand | undefined => {
    if (signed) return undefined;
    let found: GaugeBand | undefined = sorted[0];
    for (const band of sorted) if (v >= band.from) found = band;
    return found;
  };

  const capitalise = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

  const [hovered, setHovered] = useState(false);

  const band = bandAt(clamped);
  const bandName = band?.label ?? capitalise(band?.tone ?? 'neutral');

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      /*
       * `meter`, not `progressbar`. ARIA separates them: a progressbar tracks a
       * task moving towards done, a meter reports a measurement inside a known
       * range. A gauge is the second, and `ProgressBar` is correctly the first.
       */
      role="meter"
      aria-label={label ?? caption}
      /* Rounded to a tenth: the raw float reads as false precision in the a11y tree. */
      aria-valuenow={Math.round(value * 10) / 10}
      /*
       * The metric's **whole** range, not the half of it currently drawn. A
       * zero-anchored gauge redraws its domain when the reading crosses zero,
       * and a meter whose announced bounds move under the listener is worse
       * than useless — "−1.1 out of −10 to 0" reads as near the good end when it
       * is the opposite. The bar is a presentation of the range; this is the
       * range.
       */
      aria-valuemin={signed ? -bound! : lo}
      aria-valuemax={signed ? bound! : hi}
      /* The formatted reading, so a screen reader hears "+34", not "34". */
      aria-valuetext={format(value)}
    >
      {/*
        Figma `KPI` (912:35390) — a HORIZONTAL frame, figure then caption,
        `space/4` apart and centre-aligned on the cross axis, so the 12px caption
        sits on the middle of the 24px figure rather than on its baseline.

        The order is the reverse of a `KPI` metric's, which stacks its label
        *above* its value. That is the file's call and it is the right one here:
        the figure is the chart's subject and the caption is its unit, so the
        number leads and the word trails it the way "34 points" does.
      */}
      <div className={styles.readout}>
        {/*
          The figure carries the reading's own tone, which is what both NPS
          mocks draw — green for a positive score, red for a negative one. On a
          banded scale that is the band's tone; zero-anchored, it is the sign's.
        */}
        <span
          className={`${styles.value} ${valueToneClass[band?.tone ?? (negative ? 'negative' : 'positive')]}`}
        >
          {format(value)}
        </span>

        {caption !== undefined && <span className={styles.caption}>{caption}</span>}
      </div>

      {/* Figma `Progress Bar` and the scale under it, as one block — see `.plot`. */}
      <div className={styles.plot}>
        <div
          className={styles.bar}
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
        >
          {hovered && (
            <div className={styles.tooltipLayer}>
              {/*
               * Rendered directly rather than handed to a chart library, so the
               * props Recharts would clone in are supplied here. `active` is
               * always true: this only exists while `hoverAt` is set.
               */}
              <ChartTooltip
                active
                label={caption}
                payload={[
                  {
                    name: bandName,
                    value: format(value),
                    color: toneColor[band?.tone ?? (negative ? 'negative' : 'positive')],
                  },
                ]}
                compare={compare && (() => compare)}
              />
            </div>
          )}
          {Array.from({ length: SEGMENT_COUNT }, (_, i) => {
            /*
             * A negative zero-anchored bar fills from the right, because its
             * zero is the domain's *top* end and the fill grows out of zero.
             * Every other case fills from the left.
             */
            const isFilled = negative ? i >= SEGMENT_COUNT - filled : i < filled;
            return (
              <div
                key={i}
                className={`${styles.segment} ${isFilled ? toneClass[toneAt(i)] : styles.empty}`}
                aria-hidden="true"
              />
            );
          })}
        </div>

        {/* Redundant with aria-valuemin/max, so hidden from the accessibility tree. */}
        <div className={styles.scale} aria-hidden="true">
          <span>{formatEnd(lo)}</span>
          <span>{formatEnd(hi)}</span>
        </div>
      </div>
    </div>
  );
}
